import { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { PetFormHeader } from "../components/PetFormHeader";
import {
  getPetDetail,
  updateNGOtransfer,
} from "../services/api";
import DatePickerField from "../components/DatePickerField";
import { convertToISO, convertFromISO } from "../utils/dateValidation";
import {
  validatePetPhoneNumber, validateEmail
} from "../utils/validation";
import { useTranslation } from 'react-i18next';
import { transform } from "lodash";


const Label = ({ children, htmlFor, className, ...props }) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    className={className}
    variant="body2"
    color="textPrimary"
    {...props}
    sx={{ fontWeight: 700, mb: 1, ...props.sx }}
  >
    {children}
  </Typography>
);

const PetTransfer = () => {
  const [detailInfo, setDetailInfo] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const navigate = useNavigate();
  const { petId } = useParams();
  const { t } = useTranslation();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [BackColor, setBackColor] = useState("");
  const [UpdateMessage, setUpdateMessage] = useState("");
  const [petData, setPetData] = useState(location.state?.petData);
  const isMobile = useMediaQuery("(max-width:900px)");
  const [transferForm, setTransferForm] = useState({
    regDate: "",
    transferContact: "",
    UserContact: "",
    UserEmail: "",
    regPlace: "",
    transferRemark: "",
    transferOwner: "",
  });
  const [dateErrors, setDateErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [isPetTransferred, setIsPetTransferred] = useState(false);

  // Handle field changes with validation
  const handleFieldChange = (field, value) => {
    setDetailInfo({ ...detailInfo, [field]: value });


    if (field === "UserContact") {
      const phoneError = validatePetPhoneNumber(value, false);
      setFieldErrors((prev) => ({
        ...prev,
        UserContact: phoneError,
      }));
    } else {
      // Clear error when field is filled
      setFieldErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Fetch pet data if editing
  useEffect(() => {
    if (petId && petData) {
      setIsEditMode(true);
      if (petData.transferNGO && petData.transferNGO.length > 0) {
        setIsPetTransferred(petData.transferNGO[0].isTransferred);

        setTransferForm({
          ...transferForm,
          regDate: convertFromISO(petData.transferNGO[0].regDate),
          transferContact: petData.transferNGO[0].transferContact,
          UserContact: petData.transferNGO[0].UserContact,
          UserEmail: petData.transferNGO[0].UserEmail,
          regPlace: petData.transferNGO[0].regPlace,
          transferRemark: petData.transferNGO[0].transferRemark,
          transferOwner: petData.transferNGO[0].transferOwner,
        });
      }
    }
  }, [petId, petData]);

  // Import the utility function instead of duplicating it
  const convertDateForBackend = (dateString) => {
    return convertToISO(dateString);
  };

  const submitTransfer = async () => {
    try {
      setLoading(true);
      const transferData = {
        regDate: convertDateForBackend(transferForm.regDate),
        UserContact: transferForm.UserContact,
        UserEmail: transferForm.UserEmail,
        regPlace: transferForm.regPlace,
        transferRemark: transferForm.transferRemark,
        transferOwner: transferForm.transferOwner,
        // isTransferred: true,
      };
      if (transferForm.UserContact === "" || transferForm.UserEmail === "" || transferForm.transferOwner === "" || transferForm.regDate === "") {
        setLoading(false);
        setError(true);
        setBackColor("#d32f2f");
        setUpdateMessage(t('PleaseFillInRequiredFields'));
        setTimeout(() => {
          setError(false);
        }, 2000);
        return;
      }
      const response = await updateNGOtransfer(petId, transferData);

      if (response.status === 200) {
        setLoading(false);
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          navigate("/pet-management");
        }, 2000);
      }

    } catch (error) {
      if (error.status === 404 && error.response.data.phoneNumber === false) {
        setLoading(false);
        setError(true);
        setBackColor("#d32f2f");
        setUpdateMessage(t('NoUserPhone'));
        setTimeout(() => {
          setError(false);
        }, 2000);
      }
      if (error.status === 404 && error.response.data.email === false) {
        setLoading(false);
        setError(true);
        setBackColor("#d32f2f");
        setUpdateMessage(t('NoUserEmail'));
        setTimeout(() => {
          setError(false);
        }, 2000);
      }
    }
  };


  return (
    <Box
      sx={{
        backgroundColor: "#FCF8F3",
        minHeight: "100vh",
        pt: isMobile ? 12 : 16,
      }}
    >
      {/* Header */}
      <Navbar />
      <Box
        sx={{
          maxWidth: 1440,
          mx: "auto",
          mb: { xs: 5, md: 10 },
          px: { xs: 2, sm: 3, md: 0 },
        }}
      >
        {/* Tabs */}
        {/* Tabs */}
        <PetFormHeader
          currentPageId="transfer"
          isEditMode={isEditMode}
          petId={isEditMode ? petId : null}
          handlePageNavigation={() => { }}
          petData={petData}
        />
        {success ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              gap: 2,
              py: 10,
            }}
          >
            <img
              src="/icons/checkGif.gif"
              alt="Success"
              style={{
                width: "80px",
                height: "auto",
                mixBlendMode: "multiply",
              }}
            />
            <div
              style={{
                fontWeight: "700",
                fontSize: "20px",
                textAlign: "center",
                
              }}>
              {t('PetTransferredSuccessfully')}
            </div>
          </Box>
        ) : (
          <Box component="form">
            <Typography fontWeight={700} sx={{ fontSize: 25, mb: 4, px: 3 }}>
              {t("PetTransfer")}
            </Typography>

            <Grid
              container
              spacing={3}
              justifyContent="space-between"
              sx={{ px: 3, mb: 4 }}
            >
              {/* First Column */}
              <Grid item xs={12} md={4} sx={{ minWidth: isMobile ? 310 : 400, }}>
                <Box>
                  {/* regDate */}
                  <Label htmlFor="regDate" sx={{ display: "block", mb: 1 }}>
                    {t("RegistrationDate")} <span style={{ color: "red", marginLeft: 4 }}>*</span>
                  </Label>
                  <DatePickerField
                    id="regDate"
                    name="regDate"
                    placeholder="DD/MM/YYYY"
                    value={transferForm.regDate}
                    onChange={(value) =>
                      setTransferForm({
                        ...transferForm,
                        regDate: value,
                      })
                    }
                    onError={(error) =>
                      setDateErrors({ ...dateErrors, regDate: error })
                    }
                    error={!!dateErrors.regDate}
                    helperText={dateErrors.regDate}
                  />
                </Box>
                <Box className="mt-4">
                  <Label
                    htmlFor="UserContact"
                    sx={{ display: "block", mb: 1 }}
                  >
                    {t("userContact")} <span style={{ color: "red", marginLeft: 4 }}>*</span>
                  </Label>
                  <TextField
                    id="UserContact"
                    name="UserContact"
                    placeholder={t("contactNumber")}
                    value={transferForm.UserContact}
                    onChange={(e) => {
                      setTransferForm({
                        ...transferForm,
                        UserContact: e.target.value,
                      });
                      // Validate transferNGO contact
                      const phoneError = validatePetPhoneNumber(
                        e.target.value,
                        false
                      );
                      setFieldErrors((prev) => ({
                        ...prev,
                        UserContact: phoneError,
                      }));
                    }}
                    fullWidth
                    variant="outlined"
                    error={!!fieldErrors.UserContact}
                    helperText={fieldErrors.UserContact}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Box>
              </Grid>
              {/* Second Column */}
              <Grid item xs={12} md={4} sx={{ minWidth: isMobile ? 310 : 400, }}>
                <Box>
                  <Label htmlFor="regPlace" sx={{ display: "block", mb: 1 }}>
                    {t("RegistrationLocation")}
                  </Label>
                  <TextField
                    id="regPlace"
                    name="regPlace"
                    placeholder={t("RegistrationLocation")}
                    value={transferForm.regPlace}
                    onChange={(e) =>
                      setTransferForm({
                        ...transferForm,
                        regPlace: e.target.value,
                      })
                    }
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Box>
                <Box className="mt-4">
                  <Label
                    htmlFor="UserEmail"
                    sx={{ display: "block", mb: 1 }}
                  >
                    {t("userEmail")} <span style={{ color: "red", marginLeft: 4 }}>*</span>
                  </Label>
                  <TextField
                    id="UserEmail"
                    name="UserEmail"
                    placeholder="example@gmail.com"
                    value={transferForm.UserEmail}
                    onChange={(e) => {
                      setTransferForm({
                        ...transferForm,
                        UserEmail: e.target.value,
                      });
                      // Validate transferNGO contact
                      const emailError = validateEmail(
                        e.target.value,
                        t
                      );
                      setFieldErrors((prev) => ({
                        ...prev,
                        UserEmail: emailError,
                      }));
                    }}
                    fullWidth
                    variant="outlined"
                    error={!!fieldErrors.UserEmail}
                    helperText={fieldErrors.UserEmail}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Box>

              </Grid>
              {/* Third Column */}
              <Grid item xs={12} md={4} sx={{ minWidth: isMobile ? 310 : 400, }}>
                <Box>
                  <Label
                    htmlFor="transferOwner"
                    sx={{ display: "block", mb: 1 }}
                  >
                    {t("TransferOwner")} <span style={{ color: "red", marginLeft: 4 }}>*</span>
                  </Label>
                  <TextField
                    id="transferOwner"
                    name="transferOwner"
                    placeholder={t("TransferOwner")}
                    value={transferForm.transferOwner}
                    onChange={(e) =>
                      setTransferForm({
                        ...transferForm,
                        transferOwner: e.target.value,
                      })
                    }
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Box>
                <Box className="mt-4">
                  <Label
                    htmlFor="transferRemark"
                    sx={{ display: "block", mb: 1 }}
                  >
                    {t("Remarks")}
                  </Label>
                  <TextField
                    id="transferRemark"
                    name="transferRemark"
                    placeholder={t("Remarks")}
                    value={transferForm.transferRemark}
                    onChange={(e) =>
                      setTransferForm({
                        ...transferForm,
                        transferRemark: e.target.value,
                      })
                    }
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Box>
                <Grid
                  item
                  xs={12}
                  md={3}
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    gap: 2,
                    mt: 3,
                  }}
                >
                  <Button
                    variant="outlined"
                    disabled={isPetTransferred}
                    sx={{
                      color: "#FFB60C",
                      borderColor: "#FFB60C",
                      minWidth: 120,
                      cursor: loading ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                    }}
                    onClick={async () => { submitTransfer(); }}
                  >
                    {t("Transfer")}
                    {loading && (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        <CircularProgress size={15} color="inherit" />
                      </span>
                    )}
                  </Button>
                  <Button
                    variant="text"
                    color="inherit"
                    sx={{ color: "#A0A0A0", minWidth: 120, fontWeight: 700 }}
                    onClick={() => {
                      setTransferForm({
                        regDate: "",
                        UserContact: "",
                        regPlace: "",
                        transferRemark: "",
                        transferOwner: "",
                      });
                      navigate("/pet-management");
                    }}
                  >
                    {t("cancel")}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Box>)}
      </Box>
      <Footer />
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: (error) ? 20 : -400,
          backgroundColor: BackColor,
          color: "#fff",
          padding: "16px 24px",
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 9999,
          minWidth: 250,
          display: "flex",
          alignItems: "center",
          gap: 2,
          transition: (success || error)
            ? "right 0.4s ease-out"
            : "right 0.4s ease-in",
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {error ? (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 6L9 17L4 12"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </Box>
        <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
          {UpdateMessage}
        </Typography>
      </Box>
    </Box>
  );
};

export default PetTransfer;
